/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import Retracked from 'js/lib/retracked';
import user from 'js/lib/user';

import type {
  VideoCoursesQuery_CoursesV1Resource_slug_elements as CourseType,
  VideoCoursesQuery as CoursesQueryData,
  VideoCoursesQueryVariables as CoursesQueryVariables,
} from 'bundles/clips/queries/__generated__/VideoCoursesQuery';
import { org_coursera_course_CourseStatus as CourseStatus } from 'bundles/clips/queries/__generated__/globalTypes';
import { videoCoursesQuery } from 'bundles/clips/queries/graphqlQueries';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import PageHeader from 'bundles/page-header/components/PageHeader';
import withFetchLazyImages from 'bundles/page/components/shared/utils/withFetchLazyImages';
import { NotFoundError, RedirectError } from 'bundles/ssr/lib/errors/directives';

import 'css!bundles/clips/styl/index';

const COURSE_SLUG_DOES_NOT_EXIST = 'not found';

type PropsFromStores = {
  csrfToken: string;
};

type PropsFromRouter = PropsFromStores & {
  courseSlug: string;
};

type PropsFromCourses = {
  course?: CourseType | null;
};

export type Props = PropsFromRouter & PropsFromCourses;

type State = {
  componentDidMount: boolean;
};

export class ClipsApp extends React.LegacyComponentWithChildren<Props, State> {
  static contextTypes = {
    getStore: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof ClipsApp.contextTypes>;

  state = {
    componentDidMount: false,
  };

  componentDidMount() {
    this.setState(() => ({
      componentDidMount: true,
    }));
  }

  render() {
    const { children, course } = this.props;
    const { componentDidMount } = this.state;

    const { getStore } = this.context;
    const isAuthenticated = user.isAuthenticatedUser();

    if (!componentDidMount) {
      const errors = getStore && getStore('NaptimeStore').errors;
      if (
        errors &&
        Object.values(errors).some((message) => {
          if (message && typeof message === 'string') {
            const lowerCaseMessage = message.toLowerCase();
            return lowerCaseMessage.includes(COURSE_SLUG_DOES_NOT_EXIST);
          }
          return false;
        })
      ) {
        // if the slug is invalid, we're throwing an error and node render will propagate the error to edge
        // to render a static 404 page
        throw new NotFoundError();
      } else if (course && course.courseStatus !== CourseStatus.launched) {
        throw new RedirectError(301, `/learn/${course.slug}`);
      }
    }

    return (
      <div className="rc-VideoLoggedOutApp" data-testid="clips-app">
        <PageHeader isPageWithSecondaryCta={true} hideMetaNav={isAuthenticated} />
        {children}
        <PageFooter />
      </div>
    );
  }
}

export default compose<Props, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'video_logged_out_page',
      page: 'video_lecture_landing_page',
    },
  })),
  connectToStores<{ csrfToken: string }>(['ApplicationStore'], ({ ApplicationStore }) => ({
    csrfToken: ApplicationStore.csrfToken,
  })),
  connectToRouter<{ courseSlug: string }>(({ params: { courseSlug } }) => ({
    courseSlug,
  })),
  graphql<PropsFromRouter, CoursesQueryData, CoursesQueryVariables, PropsFromCourses>(videoCoursesQuery, {
    options: ({ courseSlug }) => ({
      variables: {
        slug: courseSlug,
      },
    }),
    props: ({ data }) => {
      const course = data?.CoursesV1Resource?.slug?.elements[0];

      return { course };
    },
  }),
  withFetchLazyImages
)(ClipsApp);
